import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiConfig, msalConfig } from '../config/authConfig';
import { AuthError } from './api';

// Create MSAL instance with proper async initialization
let msalInstance: PublicClientApplication | null = null;
let msalInitPromise: Promise<void> | null = null;
const isTestEnv = process.env.NODE_ENV === 'test';

const createTestMsalInstance = (): PublicClientApplication => {
  const testAccount = {
    homeAccountId: 'test-account',
    environment: 'test',
    tenantId: 'test-tenant',
    username: 'test@example.com',
    localAccountId: 'test-account'
  } as const;

  const fakeResponse = {
    accessToken: 'test-token',
    idToken: 'test-token',
    account: testAccount
  };

  return {
    initialize: async () => undefined,
    getAllAccounts: () => [testAccount],
    setActiveAccount: () => undefined,
    loginPopup: async () => fakeResponse,
    acquireTokenPopup: async () => fakeResponse,
    acquireTokenSilent: async () => fakeResponse
  } as unknown as PublicClientApplication;
};

async function initializeMsal(): Promise<PublicClientApplication | null> {
  if (msalInstance && msalInitPromise) {
    try {
      await msalInitPromise;
      return msalInstance;
    } catch {
      msalInstance = null;
      msalInitPromise = null;
      return null;
    }
  }
  
  try {
    msalInstance = new PublicClientApplication(msalConfig);
  } catch (error) {
    if (isTestEnv) {
      msalInstance = createTestMsalInstance();
    } else {
      console.error('ShopifyService: Failed to create MSAL instance:', error);
      msalInstance = null;
      msalInitPromise = null;
      return null;
    }
  }

  if (typeof msalInstance.initialize === 'function') {
    try {
      msalInitPromise = Promise.resolve(msalInstance.initialize());
      await msalInitPromise;
    } catch (error) {
      console.warn('ShopifyService: MSAL initialize failed:', error);
      msalInstance = isTestEnv ? createTestMsalInstance() : null;
      msalInitPromise = isTestEnv ? Promise.resolve() : null;
      return msalInstance;
    }
  } else {
    msalInitPromise = Promise.resolve();
  }

  // console.log('ShopifyService: MSAL initialized successfully');
  return msalInstance;
}

export interface ShopifyProduct {
  id: string;
  title: string;
  handle: string;
  body_html?: string;
  vendor: string;
  product_type: string;
  tags: string | string[];  // API returns comma-separated string, but can be array
  status: string;
  created_at: string;
  updated_at: string;
  // Additional product-level fields
  published_at?: string | null;
  published_scope?: string;  // 'web' or 'global'
  template_suffix?: string | null;
  variants: Array<{
    id: string;
    title: string;
    price: string;
    sku: string;
    inventory_quantity: number;
    weight: number;
    weight_unit: string;
    compare_at_price?: string | null;
    barcode?: string;
    grams?: number;
    requires_shipping?: boolean;
    taxable?: boolean;
    option1?: string | null;
    option2?: string | null;
    option3?: string | null;
  }>;
  images: Array<{
    id: string;
    src: string;
    alt: string;
    position?: number;
    attachment?: string; // Base64 encoded image for uploads
  }>;
  options?: Array<{
    name: string;
    values: string[];
  }>;
  // Extended metafields structure for pottery products
  metafields?: {
    dimensions?: {
      height_cm: number | null;
      width_cm: number | null;
      depth_cm: number | null;
    };
    weight_g?: number | null;
    materials?: string[];
    colors?: string[];
    technique?: string | null;
    is_unique?: boolean | null;
    artist_name?: string | null;
  };
  // Translations for multilingual content
  translations?: {
    [locale: string]: {
      title?: string;
      body_html?: string;
    };
  };
  // Extended fields for internal use
  refinedMetadata?: Record<string, unknown>;
  sharedInformation?: Record<string, unknown>;
  originalData?: Record<string, unknown>;
  correlationId?: string;
  agentSummary?: string;
  shopify_product_id?: string;
}

export interface ShopifyProductsResponse {
  success: boolean;
  products: ShopifyProduct[];
  count: number;
  next_page_info?: string | null;
  prev_page_info?: string | null;
  error?: string;
}

export interface ShopifyValidationResponse {
  success: boolean;
  shop_name?: string;
  domain?: string;
  email?: string;
  plan_name?: string;
  currency?: string;
  timezone?: string;
  error?: string;
}

export interface ShopifyProductResponse {
  success: boolean;
  product_id?: string;
  product_data?: {
    id: string;
    title: string;
    handle: string;
    status: string;
  };
  error?: string;
}

export interface ProductData {
  title?: string;
  body_html?: string;
  vendor?: string;
  product_type?: string;
  tags?: string[];
  status?: string;
  variants?: Array<{
    title?: string;
    price?: string;
    sku?: string;
    inventory_quantity?: number;
    weight?: number;
    weight_unit?: string;
  }>;
  images?: string[];
  metafields?: {
    dimensions?: {
      height_cm: number;
      width_cm: number;
      depth_cm: number;
    };
    materials?: string[];
    colors?: string[];
  };
}

export interface ShopifyStats {
  total_products: number;
  active_products: number;
  draft_products: number;
  total_variants: number;
  total_inventory: number;
  unique_pieces: number;
  limited_series: number;
  categories: Record<string, number>;
  recent_activity: {
    products_added_this_week: number;
    products_updated_this_week: number;
    low_inventory_items: number;
  };
  inventory_summary: {
    in_stock: number;
    low_stock: number;
    out_of_stock: number;
  };
  source?: string;
  last_updated?: string;
}

export interface ShopifyStatsResponse {
  stats: ShopifyStats;
  last_updated: string;
}

export interface ProductNameValidationResponse {
  success: boolean;
  exists: boolean;
  message: string;
  existing_product?: {
    id: string;
    title: string;
    handle: string;
  };
  error?: string;
}

export interface TranslationGenerationResult {
  product_id: string;
  enabled_locales: string[];
  translations_generated: Array<{
    language: string;
    title?: string;
    body_html?: string;
  }>;
  translations_saved: string[];
  translations_updated: string[];
  skipped: string[];
  errors: string[];
}

export interface TranslationGenerationResponse {
  success: boolean;
  message?: string;
  results?: TranslationGenerationResult;
  error?: string;
}

export interface TranslationUpdateData {
  title?: string;
  body_html?: string;
}

export interface TranslationUpdateResponse {
  success: boolean;
  message?: string;
  translations?: Array<{
    key: string;
    value: string;
    locale: string;
  }>;
  error?: string;
}

class ShopifyService {
  private async getAuthHeaders(forceInteractive = false): Promise<Record<string, string>> {
    try {
      const msal = await initializeMsal();
      if (!msal) {
        throw new AuthError('Authentifizierungsdienst ist nicht verfügbar.');
      }

      const scopes = ['openid', 'profile', 'email', 'User.Read'];

      const interactiveAcquire = async (): Promise<string> => {
        let accounts = msal.getAllAccounts();
        if (!accounts || accounts.length === 0) {
          const loginResponse = await msal.loginPopup({
            scopes,
            prompt: 'select_account',
          });
          if (loginResponse.account) {
            msal.setActiveAccount(loginResponse.account);
          }
          return loginResponse.idToken || loginResponse.accessToken;
        }

        const popupResponse = await msal.acquireTokenPopup({
          scopes,
          account: accounts[0],
          prompt: 'select_account',
        });
        if (popupResponse.account) {
          msal.setActiveAccount(popupResponse.account);
        }
        return popupResponse.idToken || popupResponse.accessToken;
      };

      if (forceInteractive) {
        if (isTestEnv) {
          return {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer test-token'
          };
        }
        const token = await interactiveAcquire();
        return {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        };
      }

      let accounts = msal.getAllAccounts();
      if (!accounts || accounts.length === 0) {
        if (isTestEnv) {
          return {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer test-token'
          };
        }
        const token = await interactiveAcquire();
        return {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        };
      }

      const tokenRequest = {
        scopes,
        account: accounts[0]
      };

      try {
        const tokenResponse = await msal.acquireTokenSilent(tokenRequest);
        const token = tokenResponse.idToken || tokenResponse.accessToken;
        return {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        };
      } catch (silentError) {
        const errorCode = (silentError as { errorCode?: string })?.errorCode;
        const requiresInteraction =
          silentError instanceof InteractionRequiredAuthError ||
          errorCode === 'monitor_window_timeout' ||
          errorCode === 'block_token_requests' ||
          errorCode === 'token_renewal_error';

        if (requiresInteraction) {
          if (isTestEnv) {
            return {
              'Content-Type': 'application/json',
              'Authorization': 'Bearer test-token'
            };
          }
          const token = await interactiveAcquire();
          return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          };
        }

        throw silentError;
      }
    } catch (error) {
      console.error('Failed to get auth token:', error);
      throw new AuthError('Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.');
    }
  }

  private normalizeHeaders(headers?: HeadersInit): Record<string, string> {
    if (!headers) {
      return {};
    }
    if (headers instanceof Headers) {
      const result: Record<string, string> = {};
      headers.forEach((value, key) => {
        result[key] = value;
      });
      return result;
    }
    if (Array.isArray(headers)) {
      return headers.reduce<Record<string, string>>((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});
    }
    return { ...headers };
  }

  private async fetchWithAuthRetry(url: string, init: RequestInit = {}): Promise<Response> {
    const additionalHeaders = this.normalizeHeaders(init.headers);
    let baseHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
    let usedAuth = false;

    try {
      baseHeaders = await this.getAuthHeaders();
      usedAuth = true;
    } catch (authError) {
      console.warn('ShopifyService: proceeding without auth headers due to error:', authError);
    }

    const response = await fetch(url, {
      ...init,
      headers: { ...baseHeaders, ...additionalHeaders },
    });

    if (response.status === 401 && usedAuth) {
      const refreshedHeaders = await this.getAuthHeaders(true);
      return fetch(url, {
        ...init,
        headers: { ...refreshedHeaders, ...additionalHeaders },
      });
    }

    return response;
  }

  /**
   * Public method to get auth headers for use by other components
   */
  async getAuthHeadersPublic(): Promise<HeadersInit> {
    return this.getAuthHeaders();
  }

  /**
   * Validate Shopify connection
   */
  async validateConnection(): Promise<ShopifyValidationResponse> {
    try {
      const response = await this.fetchWithAuthRetry(`${apiConfig.baseUrl}/shopify/validate`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to validate Shopify connection:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  /**
   * Get products from Shopify store
   */
  async getProducts(limit = 50, search?: string, page_info?: string): Promise<ShopifyProductsResponse> {
    try {
      let url = `${apiConfig.baseUrl}/shopify/products?limit=${limit}`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      if (page_info) {
        url += `&page_info=${encodeURIComponent(page_info)}`;
      }

      const response = await this.fetchWithAuthRetry(url, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      // The API returns {products, count, source, next_page_info, prev_page_info} - normalize to our interface
      return {
        success: true,
        products: data.products || [],
        count: data.count || 0,
        next_page_info: data.next_page_info,
        prev_page_info: data.prev_page_info
      };
    } catch (error) {
      // console.error('Failed to fetch Shopify products:', error);
      return {
        success: false,
        products: [],
        count: 0,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get a single product by ID from Shopify store
   * Fetches all products and filters by ID (since Shopify API requires individual endpoint)
   */
  async getProduct(productId: string): Promise<ShopifyProduct | null> {
    try {
      // Fetch products and find the one with matching ID
      const response = await this.getProducts(250); // Get more products to find the one we need
      if (!response.success) {
        console.error('Failed to fetch products:', response.error);
        return null;
      }

      const product = response.products.find(p => p.id === productId || p.id === `gid://shopify/Product/${productId}`);
      return product || null;
    } catch (error) {
      console.error('Failed to fetch product:', error);
      return null;
    }
  }

  /**
   * Get a single product by ID with all available translations
   * Uses the dedicated endpoint that fetches translations via GraphQL
   */
  async getProductWithTranslations(productId: string): Promise<ShopifyProduct | null> {
    try {
      const response = await this.fetchWithAuthRetry(`${apiConfig.baseUrl}/shopify/products/${productId}`, {
        method: 'GET',
      });

      if (!response.ok) {
        console.error('Failed to fetch product with translations:', response.status, response.statusText);
        // Fall back to the regular getProduct method
        return this.getProduct(productId);
      }

      const data = await response.json();
      if (data.success && data.product) {
        return data.product as ShopifyProduct;
      }
      
      // Fall back to regular getProduct if the new endpoint didn't return data
      return this.getProduct(productId);
    } catch (error) {
      console.error('Failed to fetch product with translations:', error);
      // Fall back to the regular getProduct method
      return this.getProduct(productId);
    }
  }

  /**
   * Validate if a product name already exists in the catalog (case-insensitive)
   */
  async validateProductName(productName: string): Promise<ProductNameValidationResponse> {
    try {
      const headers = await this.getAuthHeaders();
      const encodedName = encodeURIComponent(productName.trim());
      const response = await fetch(`${apiConfig.baseUrl}/products/validate-name?name=${encodedName}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to validate product name:', error);
      return {
        success: false,
        exists: false,
        message: error instanceof Error ? error.message : 'Unknown error',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Create a new product in Shopify
   */
  async createProduct(productData: ProductData): Promise<ShopifyProductResponse> {
    try {
      const response = await this.fetchWithAuthRetry(`${apiConfig.baseUrl}/shopify/products`, {
        method: 'POST',
        headers: {
          'X-Correlation-Id': this.generateCorrelationId()
        },
        body: JSON.stringify({ productData })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to create Shopify product:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  /**
   * Update an existing product in Shopify
   */
  async updateProduct(productId: string, productData: ProductData): Promise<ShopifyProductResponse> {
    try {
      const response = await this.fetchWithAuthRetry(`${apiConfig.baseUrl}/shopify/products/${productId}`, {
        method: 'PUT',
        headers: {
          'X-Correlation-Id': this.generateCorrelationId()
        },
        body: JSON.stringify({ productData })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to update Shopify product:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get Shopify store statistics
   */
  async getStats(): Promise<ShopifyStatsResponse> {
    try {
      const response = await this.fetchWithAuthRetry(`${apiConfig.baseUrl}/shopify/stats`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch Shopify stats:', error);
      // Return empty stats structure on error
      const emptyStats: ShopifyStats = {
        total_products: 0,
        active_products: 0,
        draft_products: 0,
        total_variants: 0,
        total_inventory: 0,
        unique_pieces: 0,
        limited_series: 0,
        categories: {},
        recent_activity: {
          products_added_this_week: 0,
          products_updated_this_week: 0,
          low_inventory_items: 0,
        },
        inventory_summary: {
          in_stock: 0,
          low_stock: 0,
          out_of_stock: 0,
        },
        source: 'error'
      };
      
      return {
        stats: emptyStats,
        last_updated: new Date().toISOString()
      };
    }
  }

  /**
   * Generate translations for an existing Shopify product using AI
   * @param productId The Shopify product ID
   * @returns TranslationGenerationResponse with results of the translation generation
   */
  async generateTranslations(productId: string): Promise<TranslationGenerationResponse> {
    try {
      const response = await this.fetchWithAuthRetry(`${apiConfig.baseUrl}/shopify/products/${productId}/translations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Correlation-Id': this.generateCorrelationId()
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to generate translations:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Update translation for a specific locale on a Shopify product
   * @param productId The Shopify product ID
   * @param locale Target locale (en, fr, es, nl, de)
   * @param data Translation data containing title and/or body_html
   * @returns TranslationUpdateResponse with results
   */
  async updateTranslation(productId: string, locale: string, data: TranslationUpdateData): Promise<TranslationUpdateResponse> {
    try {
      const response = await this.fetchWithAuthRetry(`${apiConfig.baseUrl}/shopify/products/${productId}/translations/${locale}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Correlation-Id': this.generateCorrelationId()
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to update translation:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Delete a product from Shopify
   */
  async deleteProduct(productId: string): Promise<ShopifyProductResponse> {
    try {
      const response = await this.fetchWithAuthRetry(`${apiConfig.baseUrl}/shopify/products/${productId}`, {
        method: 'DELETE',
        headers: {
          'X-Correlation-Id': this.generateCorrelationId()
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to delete Shopify product:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  private generateCorrelationId(): string {
    return `shopify-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

export const shopifyService = new ShopifyService();
